import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Table, Switch, Tooltip, Select, Checkbox, Typography } from 'antd'
import './CreditSummaryTable.css'
import * as com from '../Common'

const PROPERTY_ACCOUNT_TYPES = [
  'Mortgage'
]
const { Paragraph } = Typography
const CreditSummaryTable = ({ 
    accounts = [], 
    initialExcludeZeroBalance = true, 
    initialExcludeClosed = true,
    loading = false,
    useCheckbox = false,
    onSelectedRowsChange,
    onExcludeZeroBalanceChange,
    onExcludeClosedChange,
    type = 'flat',
    defaultCheckedType,
    onSection3InfoChange,
    onOwnershipChange,
}) => {
  const [excludeZeroBalance, setExcludeZeroBalance] = useState(initialExcludeZeroBalance)
  const [excludeClosed, setExcludeClosed] = useState(initialExcludeClosed)
  const [isExcludedVisible, setIsExcludedVisible] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => {
    if (useCheckbox && type === 'grouped' && accounts && accounts.length > 0 && defaultCheckedType) {
      const filtered = accounts.map((account, index) => ({
        ...account,
        key: account.key || index,
      })).filter(account => {
        if (initialExcludeZeroBalance && Number(account.unpaidBalanceAmount) === 0) return false
        if (initialExcludeClosed && account.accountStatus === 'Closed') return false
        return true
      })
      const section2cKeys = filtered.filter(acc => !PROPERTY_ACCOUNT_TYPES.includes(acc.accountType)).map(acc => acc.key)
      const section3Keys = filtered.filter(acc => PROPERTY_ACCOUNT_TYPES.includes(acc.accountType)).map(acc => acc.key)
      if (defaultCheckedType === 'all') {
        return [...section2cKeys, ...section3Keys]
      } else if (defaultCheckedType === 'section2c') {
        return section2cKeys
      } else if (defaultCheckedType === 'section3') {
        return section3Keys
      }
    }
    return []
  })
  const [hoveredRow, setHoveredRow] = useState(null)
  const [selectOpen, setSelectOpen] = useState(false)
  const [mainCurrentPage, setMainCurrentPage] = useState(1)
  const [excludedCurrentPage, setExcludedCurrentPage] = useState(1)
  const [mainPageSize, setMainPageSize] = useState(10)
  const [excludedPageSize, setExcludedPageSize] = useState(5)
  const [section2cCollapsed, setSection2cCollapsed] = useState(true);
  const [section3Collapsed, setSection3Collapsed] = useState(true);
  const section3Ref = useRef(null);
  const [sorter, setSorter] = useState({ field: null, order: null });
  const { displayedAccounts, excludedAccounts, totalAccounts } = useMemo(() => {
    const filtered = accounts.map((account, index) => ({
        ...account,
        key: account.key || index,
    })).filter(account => {
        if (excludeZeroBalance && Number(account.unpaidBalanceAmount) === 0) return false
        if (excludeClosed && account.accountStatus === 'Closed') return false
        return true
    })

    const excluded = accounts.map((account, index) => ({
        ...account,
        key: account.key || `excluded-${index}`,
    })).filter(account => {
        if (excludeZeroBalance && Number(account.unpaidBalanceAmount) === 0) return true
        if (excludeClosed && account.accountStatus === 'Closed') return true
        return false
    })

    return {
      displayedAccounts: filtered,
      excludedAccounts: excluded,
      totalAccounts: filtered.length
    }
  }, [accounts, excludeZeroBalance, excludeClosed])

  const section2cAccounts = displayedAccounts.filter(
    acc => !PROPERTY_ACCOUNT_TYPES.includes(acc.accountType)
  );
  const section3Accounts = displayedAccounts.filter(
    acc => PROPERTY_ACCOUNT_TYPES.includes(acc.accountType)
  );

  const section2cSelected = section2cAccounts.length > 0 && section2cAccounts.every(a => selectedRowKeys.includes(a.key));
  const section3Selected = section3Accounts.length > 0 && section3Accounts.every(a => selectedRowKeys.includes(a.key));

  const handleSection2cSelect = (checked) => {
    let newKeys = [...selectedRowKeys];
    const keys = section2cAccounts.map(a => a.key);
    if (checked) {
      newKeys = Array.from(new Set([...newKeys, ...keys]));
    } else {
      newKeys = newKeys.filter(k => !keys.includes(k));
    }
    setSelectedRowKeys(newKeys);
    if (onSelectedRowsChange) {
      const allRows = displayedAccounts.filter(a => newKeys.includes(a.key));
      onSelectedRowsChange(allRows);
    }
  };
  const handleSection3Select = (checked) => {
    let newKeys = [...selectedRowKeys];
    const keys = section3Accounts.map(a => a.key);
    if (checked) {
      newKeys = Array.from(new Set([...newKeys, ...keys]));
    } else {
      newKeys = newKeys.filter(k => !keys.includes(k));
    }
    setSelectedRowKeys(newKeys);
    if (onSelectedRowsChange) {
      const allRows = displayedAccounts.filter(a => newKeys.includes(a.key));
      onSelectedRowsChange(allRows);
    }
  };

  const handleExcludeZeroBalanceChange = (checked) => {
    setExcludeZeroBalance(checked)
    // setSelectedRowKeys([])
    // if (onSelectedRowsChange) {
    //   onSelectedRowsChange([])
    // }
    if (onExcludeZeroBalanceChange) {
      onExcludeZeroBalanceChange(checked)
    }
  }

  const handleExcludeClosedChange = (checked) => {
    setExcludeClosed(checked)
    // setSelectedRowKeys([])
    // if (onSelectedRowsChange) {
    //   onSelectedRowsChange([])
    // }
    if (onExcludeClosedChange) {
      onExcludeClosedChange(checked)
    }
  }
  const ownershipOptions = [
    { value: 'Borrower', label: 'Borrower' },
    { value: 'CoBorrower', label: 'Co-borrower' },
    { value: 'Joint', label: 'Joint' },
  ]
  const columns = [
    {
      title: (
        <div className="table-header-wrap">Account Type</div>
      ),
      dataIndex: 'accountType',
      key: 'category',
      width: '15%',
      sorter: (a, b) => (a.accountType || '').localeCompare(b.accountType || ''),
      ellipsis: {
        showTitle: false
      },
      render: (text, record) => {
        const tooltipText = record.loanType ? `${text === 'Unkown' ? 'Other' : text} (${record.loanType})` : text === 'Unkown' ? 'Other' : text;
        return (
          <Tooltip title={tooltipText}>
            <div className="ellipsis-cell">{text === 'Unkown' ? 'Other' : text}</div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Company Name',
      dataIndex: 'creditorName',
      key: 'companyName',
      width: '15%',
      ellipsis: {
        showTitle: false
      },
      render: (text) => {
        if (!text || typeof text !== 'string') {
          return (
            <Tooltip title="-">
              <div className="ellipsis-cell">-</div>
            </Tooltip>
          );
        }
        // const formattedText = text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
        return (
          <Tooltip title={text}>
            <div className="ellipsis-cell">{text}</div>
          </Tooltip>
        );
      }
    },
    {
      title: 'Account Number',
      dataIndex: 'accountIdentifier',
      key: 'accountNumber',
      width: '15%',
      ellipsis: {
        showTitle: false
      },
      render: (text) => (
        <Tooltip title={text}>
          <div className="ellipsis-cell">{text}</div>
        </Tooltip>
      )
    },
    {
      title: 'Unpaid Balance',
      dataIndex: 'unpaidBalanceAmount',
      key: 'balance',
      width: '15%',
      sorter: (a, b) => Number(a.unpaidBalanceAmount) - Number(b.unpaidBalanceAmount),
      ellipsis: {
        showTitle: false
      },
      render: (text) => (
        <div className="ellipsis-cell">${com.commaize(text)}</div>
      )
    },
    {
      title: 'Mo. Payment',
      dataIndex: 'monthlyPaymentAmount',
      key: 'payment',
      width: '15%',
      sorter: (a, b) => Number(a.monthlyPaymentAmount) - Number(b.monthlyPaymentAmount),
      ellipsis: {
        showTitle: false
      },
      render: (text) => (
        <div className="ellipsis-cell">{Number(text) === 0 ? '-' : `$${com.commaize(text)}`}</div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'accountStatus',
      sorter: (a, b) => {
        const order = { Open: 1, Closed: 2 };
        return (order[a.accountStatus] || 99) - (order[b.accountStatus] || 99);
      },
      width: '10%',
      ellipsis: {
        showTitle: false
      },
      render: (text) => (
        <div className="ellipsis-cell">{text}</div>
      )
    },
    {
      title: 'Ownership',
      dataIndex: 'ownership',
      key: 'ownership',
      width: '15%',
      sorter: (a, b) => {
        const order = { Borrower: 1, CoBorrower: 2, Joint: 3 };
        return (order[a.ownership] || 99) - (order[b.ownership] || 99);
      },
      ellipsis: {
        showTitle: false
      },
      render: (text, record) => (
        <div
          className="ownership-cell"
          onMouseEnter={() => setHoveredRow(record.key)}
          onMouseLeave={() => {
            if (!selectOpen) {
              setHoveredRow(null)
            }
          }}
        >
          {type !== 'grouped' && hoveredRow === record.key ? (
            <Select
              defaultValue={text}
              style={{ 
                width: '100%',
                height: '32px',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: 'normal',
                fontFamily: 'Inter'
              }}
              options={ownershipOptions}
              onClick={(e) => e.stopPropagation()}
              onDropdownVisibleChange={(open) => {
                setSelectOpen(open)
                if (!open) {
                  setHoveredRow(null)
                }
              }}
              onChange={(value) => {
                setSelectOpen(false)
                setHoveredRow(null)
                if (onOwnershipChange) {
                  onOwnershipChange({ ...record, ownership: value });
                }
              }}
              suffixIcon={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M15.6622 8.65441L14.6016 7.59375L10.006 12.1893L5.41222 7.59559L4.35156 8.65625L8.94531 13.25L8.94471 13.2506L10.0054 14.3113L10.006 14.3107L10.0084 14.3131L11.0691 13.2524L11.0666 13.25L15.6622 8.65441Z" fill="#6E6E70"/>
                </svg>
              }
              autoFocus
            />
          ) : (
            <Tooltip title={text === 'CoBorrower' ? 'Co-borrower' : text}>
              <div className="ellipsis-cell">{text === 'CoBorrower' ? 'Co-borrower' : text}</div>
            </Tooltip>
          )}
        </div>
      )
    }
  ]
  const getArrowiIcon = (direction) => {
    if (direction === 'left') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M11.0303 3.96967C11.3232 4.26256 11.3232 4.73744 11.0303 5.03033L7.06066 9L11.0303 12.9697C11.3232 13.2626 11.3232 13.7374 11.0303 14.0303C10.7374 14.3232 10.2626 14.3232 9.96967 14.0303L5.46967 9.53033C5.17678 9.23744 5.17678 8.76256 5.46967 8.46967L9.96967 3.96967C10.2626 3.67678 10.7374 3.67678 11.0303 3.96967Z" fill="currentColor"/>
        </svg>
      )
    }
    if (direction === 'right') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M6.96967 14.0303C6.67678 13.7374 6.67678 13.2626 6.96967 12.9697L10.9393 9L6.96967 5.03033C6.67678 4.73744 6.67678 4.26256 6.96967 3.96967C7.26256 3.67678 7.73744 3.67678 8.03033 3.96967L12.5303 8.46967C12.8232 8.76256 12.8232 9.23744 12.5303 9.53033L8.03033 14.0303C7.73744 14.3232 7.26256 14.3232 6.96967 14.0303Z" fill="currentColor"/>
        </svg>
      )
    }
    return null
  }
  const itemRender = (page, type, originalElement) => {
    if (type === 'prev') {
      return (
        <div className="credit-table-pagination-btn prev-btn">
          {getArrowiIcon('left')}
          <div className="pagination-text">Previous</div>
        </div>
      );
    }
    if (type === 'next') {
      return (
        <div className="credit-table-pagination-btn next-btn">
          <div className="pagination-text">Next</div>
          {getArrowiIcon('right')}
        </div>
      );
    }
    if (type === 'jump-prev' || type === 'jump-next') {
      return <span style={{ color: '#222', fontWeight: '400', fontSize: '14px', lineHeight: '18px' }}>...</span>
    }
    return originalElement;
  };

  const mainPaginationConfig = {
    current: mainCurrentPage,
    pageSize: mainPageSize,
    total: displayedAccounts.length,
    showSizeChanger: false,
    onChange: (page, pageSize) => {
      setMainCurrentPage(page)
      setMainPageSize(pageSize)
    },
    itemRender,
  }

  const excludedPaginationConfig = {
    current: excludedCurrentPage,
    pageSize: excludedPageSize,
    total: excludedAccounts.length,
    showSizeChanger: false,
    onChange: (page, pageSize) => {
      setExcludedCurrentPage(page)
      setExcludedPageSize(pageSize)
    },
    itemRender,
  };

  const sortedAccounts = [...displayedAccounts];
  if (type === 'grouped' && sorter.field && sorter.order) {
    const columnSorter = columns.find(c => c.dataIndex === sorter.field || c.key === sorter.field)?.sorter;
    if (typeof columnSorter === 'function') {
      sortedAccounts.sort((a, b) => {
        const result = columnSorter(a, b);
        return sorter.order === 'ascend' ? result : -result;
      });
    }
  }

  const sortedSection2cAccounts = sortedAccounts.filter(a => !PROPERTY_ACCOUNT_TYPES.includes(a.accountType));
  const sortedSection3Accounts = sortedAccounts.filter(a => PROPERTY_ACCOUNT_TYPES.includes(a.accountType));

  const CustomBody = () => (
    <tbody>
      {/* Section2c Header */}
      <tr className="section-header ant-table-row">
        {!useCheckbox && <th className="ant-table-cell" style={{ width: 48, background: '#F7F8FF' }}></th>}
        <td colSpan={columns.length + 1} style={{ background: '#F7F8FF', fontWeight: 600, borderBottom: '1px solid #ECECEC' }}>
          {useCheckbox && (
            <Checkbox
              checked={section2cSelected}
              indeterminate={section2cAccounts.some(a => selectedRowKeys.includes(a.key)) && !section2cSelected}
              onChange={e => handleSection2cSelect(e.target.checked)}
            />
          )}
          <span
            className='section-header-title'
            onClick={() => setSection2cCollapsed(!section2cCollapsed)}
          >
            Section2c: Liabilities – Credit Cards, Other Debts, and Leases that You Owe ({section2cAccounts.length})
            <img
              src='/images/credit-summary-arrow.svg'
              style={{
                transform: section2cCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                marginLeft: 8
              }}
              alt="toggle"
            />
          </span>
        </td>
      </tr>
      {/* Section2c Rows */}
      {!section2cCollapsed && sortedSection2cAccounts.map((row, idx) => (
        <tr key={row.key} className="credit-summary-row ant-table-row">
          {useCheckbox && (
            <td className="ant-table-cell" style={{ width: 48 }}>
              <Checkbox
                checked={selectedRowKeys.includes(row.key)}
                onChange={e => {
                  let newKeys = e.target.checked
                    ? [...selectedRowKeys, row.key]
                    : selectedRowKeys.filter(k => k !== row.key);
                  setSelectedRowKeys(newKeys);
                  if (onSelectedRowsChange) {
                    const allRows = displayedAccounts.filter(a => newKeys.includes(a.key));
                    onSelectedRowsChange(allRows);
                  }
                }}
              />
            </td>
          )}
          {columns.map((col, colIdx) => (
            <td key={col.key || col.dataIndex} className="ant-table-cell" style={{ maxWidth: col.width }}>
              {col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}
            </td>
          ))}
        </tr>
      ))}
      {/* Section3 Header */}
      <tr className="section-header ant-table-row">
        {!useCheckbox && <th className="ant-table-cell" style={{ width: 48, background: '#F7F8FF' }}></th>}
        <td colSpan={columns.length + 1} style={{ background: '#F7F8FF', fontWeight: 600, borderBottom: !section3Collapsed ? '1px solid #ECECEC' : 'none' }}>
          {useCheckbox && (
            <Checkbox
              checked={section3Selected}
              indeterminate={section3Accounts.some(a => selectedRowKeys.includes(a.key)) && !section3Selected}
              onChange={e => handleSection3Select(e.target.checked)}
            />
          )}
          <span
            className='section-header-title'
            onClick={() => setSection3Collapsed(!section3Collapsed)}
          >
            Section 3. Property You Own- Mortgage Loans ({section3Accounts.length})
            <img
              src='/images/credit-summary-arrow.svg'
              style={{
                transform: section3Collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                marginLeft: 8
              }}
              alt="toggle"
            />
          </span>
        </td>
      </tr>
      {/* Section3 Rows */}
      {!section3Collapsed && sortedSection3Accounts.map((row, idx) => (
        <tr key={row.key} className="credit-summary-row ant-table-row">
          {useCheckbox && (
            <td className="ant-table-cell" style={{ width: 48 }}>
              <Checkbox
                checked={selectedRowKeys.includes(row.key)}
                onChange={e => {
                  let newKeys = e.target.checked
                    ? [...selectedRowKeys, row.key]
                    : selectedRowKeys.filter(k => k !== row.key);
                  setSelectedRowKeys(newKeys);
                  if (onSelectedRowsChange) {
                    const allRows = displayedAccounts.filter(a => newKeys.includes(a.key));
                    onSelectedRowsChange(allRows);
                  }
                }}
              />
            </td>
          )}
          {columns.map((col, colIdx) => (
            <td key={col.key || col.dataIndex} className="ant-table-cell" style={{ maxWidth: col.width }}>
              {col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  useEffect(() => {
    if (useCheckbox && type === 'grouped' && onSelectedRowsChange && selectedRowKeys.length > 0) {
      const selectedRows = displayedAccounts.filter(a => selectedRowKeys.includes(a.key))
      onSelectedRowsChange(selectedRows)
    }
  }, [accounts])

  useEffect(() => {
    if (onSection3InfoChange) {
      onSection3InfoChange({
        count: section3Accounts.length,
        section3Selected,
        handleSection3Select,
      });
    }
  }, [section3Accounts.length, section3Selected]);

  return (
    <div className="credit-summary-wrapper">
      <div className="filters-section">
        <div className="filters-section-left">
            <div className="filter-group">
                <Switch
                    className='credit-summary-switch'
                    checked={excludeZeroBalance}
                    onChange={handleExcludeZeroBalanceChange}
                />
                <div>Exclude $0 remaining balance accounts</div>
            </div>
            <div className="filter-group">
                <Switch
                    className='credit-summary-switch'
                    checked={excludeClosed}
                    onChange={handleExcludeClosedChange}
                />
                <div>Exclude closed account</div>
            </div>
        </div>
        <div className="accounts-count">
          {type === 'grouped' ? `${selectedRowKeys.length} accounts selected` : `${totalAccounts} accounts`}
        </div>
      </div>

      <Table
        className="credit-summary-table"
        loading={loading}
        rowSelection={useCheckbox ? {
          selectedRowKeys,
          onChange: (newSelectedRowKeys, selectedRows) => {
            setSelectedRowKeys(newSelectedRowKeys)
            if (onSelectedRowsChange) {
              onSelectedRowsChange(selectedRows)
            }
          },
          checkStrictly: false,
        } : undefined}
        columns={columns}
        dataSource={type === 'grouped' ? sortedAccounts : displayedAccounts}
        pagination={type === 'flat' ? mainPaginationConfig : false}
        components={type === 'grouped' ? {
          body: {
            wrapper: CustomBody
          }
        } : undefined}
        onChange={(pagination, filters, sorterObj) => {
          setSorter({
            field: sorterObj.field,
            order: sorterObj.order,
            columnKey: sorterObj.columnKey,
          });
        }}
      />

      {/* Excluded accounts table（平级渲染） */}
      {excludedAccounts.length > 0 && (
        <div>
          <div 
            className="excluded-accounts-toggle"
            style={!isExcludedVisible ? {borderRadius: '8px', borderBottom: 'none'} : {borderRadius: '8px 8px 0 0', borderBottom: '1px solid #ECECEC'}}
            onClick={() => setIsExcludedVisible(!isExcludedVisible)}
          >
            <div>{isExcludedVisible ? 'Hide' : 'Show'} excluded accounts<img src='/images/dti-calculator-arrow.svg' style={{transform: isExcludedVisible ? 'rotate(180deg)' : 'rotate(0deg)', marginLeft: '8px'}}/></div>
            <div>{excludedAccounts.length} accounts</div>
          </div>
          {isExcludedVisible && (
            <Table
              columns={columns}
              dataSource={excludedAccounts}
              pagination={excludedPaginationConfig}
              showHeader={true}
              className="excluded-accounts-table"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CreditSummaryTable